import { useCallback, useState } from 'react';
import { Message } from '@/types';
/**
 * useMessageActions Hook
 * 管理消息的复制、重试和删除操作
 */
export function useMessageActions(
  message: Message,
  onRetry?: () => void,
  onDelete?: () => void
) {
  const [copied, setCopied] = useState(false);
  /**
   * Copy message content to clipboard
   * Filters out thinking blocks and reference sections
   */
  const handleCopy = useCallback(async () => {
    try {
      let contentToCopy = '';
      // Extract content from choices (use active choice if available, otherwise first choice)
      if (message.choices && message.choices.length > 0) {
        const choiceIndex = message.activeChoice ?? 0;
        const choice = message.choices[choiceIndex];
        if (choice?.blocks) {
          const nonThinkBlocks = choice.blocks.filter(
            block => block.type === 'text'
          );
          contentToCopy = nonThinkBlocks.map(block => block.content).join('');
        }
      }
      // Remove <sup>...</sup> content and Reference: sections
      contentToCopy = contentToCopy
        // Remove all <sup>...</sup> tags and their content
        .replace(/<sup[^>]*>.*?<\/sup>/gi, '')
        // Remove Reference: sections (from "Reference:" to end of line or next section)
        .replace(/## Reference:\s*[\s\S]*?(?=\n\n|\n#|\n\*|$)/gi, '')
        // Clean up extra whitespace and line breaks
        .replace(/\n\s*\n\s*\n/g, '\n\n')
        .trim();
      await navigator.clipboard.writeText(contentToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, message.activeChoice, message.choices]);
  /**
   * Handle copy with event handling
   */
  const handleCopyClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      await handleCopy();
    },
    [handleCopy]
  );
  /**
   * Handle retry with event handling
   */
  const handleRetry = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (onRetry) {
        onRetry();
      }
    },
    [onRetry]
  );
  /**
   * Handle delete with event handling
   */
  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (onDelete) {
        onDelete();
      }
    },
    [onDelete]
  );
  return {
    handleCopy,
    handleCopyClick,
    handleRetry,
    handleDelete,
    copied,
  };
}
